import React, { useCallback, useEffect, useState } from 'react';
import { Wrapper } from './style';
import { DatePicker, message, Select, Alert, Button, Spin, Input, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { MakeApiCall } from '../../../apis';
import AllAlerts from './lib/allAlerts';
import EmailAlerts from './lib/alerts';
import { GetMarketplaceDropdown, GetMarketplaceList, rangePresets } from '../../../config';
import Settings from './lib/settings';
import AddNewAlerts from './lib/AddNewAlerts';
import { Helmet } from 'react-helmet';
import dayjs from 'dayjs';
import SyncToast from "../../../components/toast/SyncToast";
import SetupStepsBanner from "../../../components/onboarding/Setupstepsbanner";
import FeatureLockedOverlay from "../../../components/modal/Featurelockedoverlay";
import { FilterOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;

const AMAZON_DATE_FORMAT = 'YYYY-MM-DD';

function Alerts({ userData }) {
    const isSpApiConnected = userData?.user?.user_data?.is_amz_sp_api_connected === 1;
    const isDataSyncing = isSpApiConnected && userData?.user?.user_data?.is_data_sync_email_sent === null;
    const trialExpired = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);

    const [activeTab, setActiveTab] = useState('allAlerts');
    const [totalPage, setTotalPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);

    const [allAlerts, setAllAlerts] = useState([]);
    const [alerts, setAlerts] = useState([]);

    const [marketplaceList, setMarketplaceList] = useState([]);
    const [selectedMarketplace, setSelectedMarketplace] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [currencyList, setCurrencyList] = useState([]);
    const [selectedCurrencySign, setSelectedCurrencySign] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);

    const [asinList, setAsinList] = useState([]);
    const [selectedAsins, setSelectedAsins] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSeverity, setSelectedSeverity] = useState(null);

    // Settings tab filters
    const [settingsSearchText, setSettingsSearchText] = useState('');
    const [selectedReceiveOn, setSelectedReceiveOn] = useState(null);
    const [selectedAlertType, setSelectedAlertType] = useState(null);

    const [selectedDateRange, setSelectedDateRange] = useState([
        dayjs().subtract(29, 'day'),
        dayjs(),
    ]);

    const [editing, setEditing] = useState(null);
    const [fetchAlertSettings, setFetchAlertSettings] = useState(() => () => {});

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [searchText, setSearchText] = useState('');

    const [syncToastDismissed, setSyncToastDismissed] = useState(false);

    const [filters, setFilters] = useState({
        page: 1,
        'per-page': 20,
        name: '',
        marketplace_id: null,
    });

    const [alertCategories, setAlertCategories] = useState([]);
    const [alertDataMap, setAlertDataMap] = useState({
        types: {},
        receive_on: [],
        logicTypeMap: {}
    });

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [filtersExpanded, setFiltersExpanded] = useState(false);

    // Derive alert type options from selected category
    const alertTypeOptions = selectedCategory && alertDataMap.types?.[selectedCategory]
        ? Object.entries(alertDataMap.types[selectedCategory]).map(([key, label]) => ({
            label,
            value: key
        }))
        : [];

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isDataSyncing) setSyncToastDismissed(false);
    }, [isDataSyncing]);

    // Reset alert type when category changes
    useEffect(() => {
        setSelectedAlertType(null);
    }, [selectedCategory]);

    // Collapse filters on tab switch
    useEffect(() => {
        setFiltersExpanded(false);
    }, [activeTab]);

    // CLEAR FILTERS ON TAB CHANGE - Start
    useEffect(() => {
        // Always clear both allAlerts and settings filters when tab changes
        // So, we make the logic match handleClearFilters for both tabs
        // But, the selection should match 'activeTab' state - so both blocks are applied
        // Clear common filters
        setSelectedAsins([]);
        setSelectedCategory(null);
        setSelectedSeverity(null);
        setSelectedMarketplace(null);
        setSelectedDateRange([dayjs().subtract(29, 'day'), dayjs()]); // allAlerts
        setSettingsSearchText('');
        setSelectedReceiveOn(null);
        setSelectedAlertType(null);
    }, [activeTab]);
    // CLEAR FILTERS ON TAB CHANGE - End

    const fetchProductList = async (search = '', pageNum = 1, append = false) => {
        const trialExpiredLocal = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);
        if (!isSpApiConnected || trialExpiredLocal) return;

        setIsSearching(true);
        try {
            const url = `get-products?page=${pageNum}&per-page=10${search ? `&search_text=${search}` : ''}`;
            const response = await MakeApiCall(url, 'get', null, true, {}, userData?.token);

            if (response?.status) {
                const records = response?.data?.records || [];
                const formattedData = records.map(item => ({
                    asin: item.asin,
                    name: item.item_name,
                    image: item.item_image,
                    sku: item.sku,
                }));
                setAsinList(prev => append ? [...prev, ...formattedData] : formattedData);
                setHasMore(records.length === 10);
                setPage(pageNum);
            }
        } catch (error) {
            message.error('Error fetching products.');
        } finally {
            setIsSearching(false);
        }
    };

    const handlePopupScroll = useCallback((e) => {
        const { target } = e;
        const isAtBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 10;
        if (isAtBottom && !isSearching && hasMore) {
            setPage((prevPage) => {
                const nextPage = prevPage + 1;
                fetchProductList(searchText, nextPage, true);
                return nextPage;
            });
        }
    }, [isSearching, hasMore, searchText]);

    const debouncedSearch = useCallback(
        (() => {
            let timeout;
            return (val) => {
                clearTimeout(timeout);
                timeout = setTimeout(() => {
                    const searchVal = val || '';
                    setSearchText(searchVal);
                    setPage(1);
                    setHasMore(true);
                    fetchProductList(searchVal, 1, false);
                }, 500);
            };
        })(), [userData?.token, isSpApiConnected]
    );

    const GetMarketplace = async () => {
        const trialExpiredLocal = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);
        if (!isSpApiConnected || trialExpiredLocal) return;
        const marketplace = await GetMarketplaceDropdown();

        if (marketplace?.marketplace_list?.length > 0) {
            const selectedMarketplaceEntry = Object.entries(marketplace.selected_marketplace || {})[0];
            const [marketplaceId] = selectedMarketplaceEntry || [];
            const firstMarketplace = marketplace.marketplace_list.find((m) => m.id === marketplaceId);

            setMarketplaceList(marketplace.marketplace_list);
            setSelectedMarketplace(firstMarketplace);
            setFilters((prev) => ({ ...prev, marketplace_id: marketplaceId }));
        } else {
            setLoading(false);
        }
    };

    const fetchAlertMaster = async () => {
        const trialExpiredLocal = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);
        if (!isSpApiConnected || trialExpiredLocal) return;
        setLoading(true);
        try {
            const response = await MakeApiCall("get-alert-master-data", "get", null, true, {}, userData?.token);
            if (response?.status) {
                const { alert_catalogs_data, alert_type, alert_receive_on, alert_logic_type } = response.data;
                const categories = Object.entries(alert_catalogs_data).map(([key, label]) => ({ label, value: key }));
                setAlertCategories(categories);
                setAlertDataMap({
                    types: alert_type,
                    receive_on: alert_receive_on,
                    logicTypeMap: alert_logic_type
                });
            } else {
                message.warning(response?.message || "Failed to load alert data");
            }
        } catch (error) {
            message.error("Error fetching alert master data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isSpApiConnected) {
            GetMarketplace();
            fetchProductList('', 1, false);
            fetchAlertMaster();
        } else {
            setLoading(false);
        }
    }, [isSpApiConnected]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const response = await MakeApiCall(
                editing ? `update-alert/${editing.id}` : "add-alert",
                "post",
                values,
                true,
                {},
                userData?.token
            );
            if (response?.status) {
                message.success(editing ? "Alert updated successfully!" : "Alert added successfully!");
                setIsModalOpen(false);
                setActiveTab('settings');
                fetchAlertSettings();
            } else {
                message.error(response?.message || (editing ? "Failed to update alert." : "Failed to add alert."));
            }
        } catch (error) {
            message.error("An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const getActiveFiltersCount = () => {
        let count = 0;
        if (activeTab === 'allAlerts') {
            if (selectedAsins?.length > 0) count++;
            if (selectedCategory) count++;
            if (selectedSeverity) count++;
            if (selectedMarketplace?.id) count++;
            if (selectedDateRange?.[0] && selectedDateRange?.[1]) count++;
        } else if (activeTab === 'settings') {
            if (settingsSearchText) count++;
            if (selectedCategory) count++;
            if (selectedSeverity) count++;
            if (selectedReceiveOn) count++;
            if (selectedAlertType) count++;
            if (selectedAsins?.length > 0) count++;
        }
        return count;
    };

    const activeFiltersCount = getActiveFiltersCount();

    // handleClearFilters unchanged
    const handleClearFilters = () => {
        if (activeTab === 'allAlerts') {
            setSelectedAsins([]);
            setSelectedCategory(null);
            setSelectedSeverity(null);
            setSelectedMarketplace(null);
            setSelectedDateRange([dayjs().subtract(29, 'day'), dayjs()]);
        } else if (activeTab === 'settings') {
            setSettingsSearchText('');
            setSelectedCategory(null);
            setSelectedSeverity(null);
            setSelectedReceiveOn(null);
            setSelectedAlertType(null);
            setSelectedAsins([]);
        }
    };

    const sharedFilterStyle = { width: '100%' };
    const flexItem = (mobile) => ({
        flex: mobile ? '1 1 100%' : '0 0 15%',
        minWidth: 0,
        width: mobile ? '100%' : 'auto'
    });

    const alertTypeFilterSelect = (
        <Select
            placeholder="Select Type"
            size="large"
            allowClear
            disabled={!isSpApiConnected || !selectedCategory}
            value={selectedAlertType}
            onChange={(val) => setSelectedAlertType(val || null)}
            style={sharedFilterStyle}
            notFoundContent={!selectedCategory ? "Select a category first" : "No types found"}
        >
            {alertTypeOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
            ))}
        </Select>
    );

    return (
        <Wrapper>
            <Helmet><title>Alerts</title></Helmet>
            <SetupStepsBanner userData={userData} />

            <SyncToast
                visible={isDataSyncing && !syncToastDismissed}
                onDismiss={() => setSyncToastDismissed(true)}
            />

            <div style={{ position: 'relative' }}>
                <FeatureLockedOverlay
                    locked={trialExpired || !isSpApiConnected}
                    title="Features Locked"
                    message={trialExpired
                        ? "Your trial has expired. Please renew your subscription to continue."
                        : "Connect your Amazon account to unlock analytics."}
                />

                {/* Tab bar */}
                <div className="mb-5 mx-md-n9 mx-n2 px-10 py-5 bg-white filter">
                    <div className="position-relative d-flex wrap justify-content-between">
                        <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 d-inline-flex">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link ${activeTab === 'allAlerts' ? 'active' : ''}`}
                                    onClick={() => {
                                        if (isSpApiConnected) {
                                            setActiveTab('allAlerts');
                                            // Filters will clear on useEffect of activeTab
                                        }
                                    }}
                                >
                                    Alert History
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
                                    onClick={() => {
                                        if (isSpApiConnected) {
                                            setActiveTab('settings');
                                            // Filters will clear on useEffect of activeTab
                                        }
                                    }}
                                >
                                    Alert Rules
                                </button>
                            </li>
                        </ul>
                        <button
                            className="btn btn-danger fs-7 px-md-5 px-3 fw-bold"
                            disabled={!isSpApiConnected}
                            onClick={() => { setEditing(null); setIsModalOpen(true); }}
                        >
                            Add New Alerts
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="mb-5 mx-md-n9 mx-0 px-md-10 px-0 filters-section filters">

                    {/* Mobile toggle */}
                    {isMobile && (
                        <button
                            className="filter-toggle-btn"
                            onClick={() => setFiltersExpanded(!filtersExpanded)}
                            style={{
                                width: '100%',
                                padding: '12px 16px',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #e9ecef',
                                borderRadius: '8px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                marginBottom: filtersExpanded ? '6px' : '0'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FilterOutlined />
                                <span style={{ fontWeight: 500 }}>Filters</span>
                                {activeFiltersCount > 0 && (
                                    <span style={{
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        borderRadius: '12px',
                                        padding: '2px 8px',
                                        fontSize: '12px',
                                        marginLeft: '4px'
                                    }}>
                                        {activeFiltersCount}
                                    </span>
                                )}
                            </div>
                            {filtersExpanded ? <UpOutlined /> : <DownOutlined />}
                        </button>
                    )}

                    {/* Filter content */}
                    <div
                        className="gap-4 filters"
                        style={{
                            display: isMobile && !filtersExpanded ? 'none' : 'flex',
                            overflowX: isMobile ? 'visible' : 'auto',
                            flexWrap: isMobile ? 'wrap' : 'nowrap',
                            whiteSpace: isMobile ? 'normal' : 'nowrap',
                            padding: isMobile ? '16px 0' : '0',
                            flexDirection: isMobile ? 'column' : 'row'
                        }}
                    >
                        <div className="d-flex align-items-center gap-4 w-100" style={{
                            gap: '1rem',
                            flexWrap: isMobile ? 'wrap' : 'nowrap',
                            minWidth: 0,
                            whiteSpace: isMobile ? 'normal' : 'nowrap',
                            flexDirection: isMobile ? 'column' : 'row'
                        }}>

                            {/* Both tabs: ASIN product search */}
                            <div style={{ flex: isMobile ? '1 1 100%' : '1 1 40%', minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
                                <Select
                                    mode="multiple"
                                    showSearch
                                    placeholder="Search or select products"
                                    size="large"
                                    value={selectedAsins}
                                    onChange={setSelectedAsins}
                                    onSearch={debouncedSearch}
                                    onPopupScroll={handlePopupScroll}
                                    loading={isSearching && isSpApiConnected}
                                    disabled={!isSpApiConnected}
                                    filterOption={false}
                                    optionLabelProp="value"
                                    maxTagCount="responsive"
                                    notFoundContent={isSearching ? <div className="text-center p-2"><Spin size="small" /></div> : null}
                                    style={sharedFilterStyle}
                                >
                                    {asinList.map(item => (
                                        <Option key={item.asin} value={item.asin}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <img
                                                    src={item.image || 'https://app.sellerview.ai/sellerview_api/images/no-product-image.png'}
                                                    alt={item.name}
                                                    className="w-50px"
                                                />
                                                <div>
                                                    <div style={{ fontWeight: '500' }}>{item.name}</div>
                                                    <div style={{ fontSize: '13px', color: '#888' }}>{item.asin}</div>
                                                    <div style={{ fontSize: '13px', color: '#888' }}>{item.sku}</div>
                                                </div>
                                            </div>
                                        </Option>
                                    ))}
                                    {isSearching && hasMore && (
                                        <Option disabled key="loading-more" className="text-center bg-light">
                                            <Spin size="small" /> <small className="ms-2">Loading more...</small>
                                        </Option>
                                    )}
                                </Select>
                            </div>

                            {/* Settings only: rule name search */}
                            {activeTab === 'settings' && (
                                <div style={flexItem(isMobile)}>
                                    {/* <Input
                                        placeholder="Search alert rules"
                                        size="large"
                                        allowClear
                                        disabled={!isSpApiConnected}
                                        value={settingsSearchText}
                                        onChange={(e) => setSettingsSearchText(e.target.value)}
                                        style={sharedFilterStyle}
                                    /> */}
                                    <div className="position-relative">
                                        <i className="ki-outline ki-magnifier text-gray-800 fs-2 position-absolute top-50 translate-middle-y ms-4" style={{zIndex:99}}/>
                                        <Input
                                            allowClear
                                            type="text"
                                            value={settingsSearchText}
                                            onChange={(e) => setSettingsSearchText(e.target.value)}
                                            disabled={!isSpApiConnected}
                                            className={`form-control text-dark border-0  fs-7 ps-12 d-flex all-search `}
                                            placeholder="Search alert rules name"
                                            style={sharedFilterStyle}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Shared: Category */}
                            <div style={flexItem(isMobile)}>
                                <Select
                                    placeholder="Select Category"
                                    size="large"
                                    loading={loading && alertCategories.length === 0}
                                    allowClear
                                    disabled={!isSpApiConnected}
                                    value={selectedCategory}
                                    onChange={(val) => setSelectedCategory(val || null)}
                                    style={sharedFilterStyle}
                                >
                                    {alertCategories.map(cat => (
                                        <Option key={cat.value} value={cat.value}>{cat.label}</Option>
                                    ))}
                                </Select>
                            </div>
                            
                             {/* Settings only: Alert Type (depends on category) */}
                             {activeTab === 'settings' && (
                                <div style={flexItem(isMobile)}>
                                    {isSpApiConnected && !selectedCategory ? (
                                        <Tooltip title="Please select the category to select alert type.">
                                            <span style={{ display: 'inline-block', width: '100%' }}>
                                                {alertTypeFilterSelect}
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        alertTypeFilterSelect
                                    )}
                                </div>
                            )}

                            {/* Shared: Severity */}
                            <div style={flexItem(isMobile)}>
                                <Select
                                    placeholder="Select Severity"
                                    size="large"
                                    allowClear
                                    disabled={!isSpApiConnected}
                                    value={selectedSeverity}
                                    onChange={(val) => setSelectedSeverity(val || null)}
                                    style={sharedFilterStyle}
                                >
                                    <Option value="Minor">Minor</Option>
                                    <Option value="Major">Major</Option>
                                    <Option value="Critical">Critical</Option>
                                </Select>
                            </div>

                           

                            {/* Settings only: Alert receive on */}
                            {activeTab === 'settings' && (
                                <div style={flexItem(isMobile)}>
                                    <Select
                                        placeholder="Select Alert Channel"
                                        size="large"
                                        allowClear
                                        disabled={!isSpApiConnected}
                                        value={selectedReceiveOn}
                                        onChange={(val) => setSelectedReceiveOn(val || null)}
                                        style={sharedFilterStyle}
                                    >
                                        {(alertDataMap.receive_on || []).map((item) => (
                                            <Option key={item.value ?? item} value={item.value ?? item}>
                                                {item.label ?? item}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                            )}

                            {/* AllAlerts only: Marketplace */}
                            {activeTab === 'allAlerts' && (
                                <div style={flexItem(isMobile)}>
                                    <Select
                                        size="large"
                                        placeholder="Select Marketplace"
                                        options={GetMarketplaceList(marketplaceList)}
                                        value={selectedMarketplace?.id}
                                        disabled={!isSpApiConnected}
                                        onChange={(id) => {
                                            const selected = marketplaceList.find((m) => m.id === id);
                                            setSelectedMarketplace(selected);
                                            setFilters({ ...filters, marketplace_id: id });
                                            setLoading(true);
                                        }}
                                        style={sharedFilterStyle}
                                    />
                                </div>
                            )}

                            {/* AllAlerts only: Date Range */}
                            {activeTab === 'allAlerts' && (
                                <div style={flexItem(isMobile)}>
                                    <RangePicker
                                        size="large"
                                        disabled={!isSpApiConnected}
                                        presets={rangePresets}
                                        value={selectedDateRange}
                                        onChange={(dates) => setSelectedDateRange(dates || [])}
                                        style={sharedFilterStyle}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Mobile: clear all */}
                    {isMobile && filtersExpanded && activeFiltersCount > 0 && (
                        <div style={{ marginTop: '12px', textAlign: 'right' }}>
                            <button
                                onClick={handleClearFilters}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#dc3545',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    padding: '4px 8px'
                                }}
                            >
                                Clear all filters
                            </button>
                        </div>
                    )}
                </div>

                {/* Tab content */}
                {activeTab === 'allAlerts' && (
                    <AllAlerts
                        userData={userData}
                        list={allAlerts}
                        loading={loading}
                        selectedAsins={selectedAsins}
                        selectedCategory={selectedCategory}
                        selectedSeverity={selectedSeverity}
                        selectedDateRange={selectedDateRange}
                        onCategoryChange={setSelectedCategory}
                    />
                )}
                {activeTab === 'alerts' && <EmailAlerts list={alerts} loading={loading} />}
                {activeTab === 'settings' && (
                    <Settings
                        exposeFetcher={setFetchAlertSettings}
                        setIsModalOpen={setIsModalOpen}
                        setEditing={setEditing}
                        userData={userData}
                        loading={loading}
                        searchText={settingsSearchText}
                        selectedCategory={selectedCategory}
                        selectedSeverity={selectedSeverity}
                        selectedReceiveOn={selectedReceiveOn}
                        selectedAlertType={selectedAlertType}
                        selectedAsins={selectedAsins}
                    />
                )}

                <AddNewAlerts
                    isModalVisible={isModalOpen}
                    handleCancel={() => { setIsModalOpen(false); setEditing(null); }}
                    handleSubmit={async (values) => {
                        await handleSubmit(values);
                        setEditing(null);
                    }}
                    marketplaceList={marketplaceList}
                    editing={editing}
                    setSelectedAsins={setSelectedAsins}
                    selectedAsins={selectedAsins}
                    asinList={asinList}
                    userData={userData}
                    alertCategories={alertCategories}
                    alertDataMap={alertDataMap}
                    fetchProductList={fetchProductList}
                    isSearching={isSearching}
                    hasMore={hasMore}
                    page={page}
                    searchText={searchText}
                />
            </div>
        </Wrapper>
    );
}

export default Alerts;